import fs from "node:fs/promises";
import path from "node:path";

import { getAppPaths } from "./paths.js";

export async function ensureAppDirectories(rootDir = process.cwd()): Promise<ReturnType<typeof getAppPaths>> {
  const paths = getAppPaths(rootDir);
  await fs.mkdir(paths.configDir, { recursive: true });
  await fs.mkdir(paths.dataDir, { recursive: true });
  await fs.mkdir(paths.cacheDir, { recursive: true });
  await fs.mkdir(paths.sessionsDir, { recursive: true });
  await fs.mkdir(paths.changesDir, { recursive: true });
  await ensureRuntimeStateIgnoredByGit(rootDir, paths.configDir);
  return paths;
}

async function ensureRuntimeStateIgnoredByGit(rootDir: string, stateDir: string): Promise<void> {
  const gitDir = await resolveGitDir(rootDir);
  if (!gitDir) {
    return;
  }

  const relativeStateDir = path.relative(path.resolve(rootDir), path.resolve(stateDir)).replace(/\\/g, "/");
  if (!relativeStateDir || relativeStateDir.startsWith("..")) {
    return;
  }

  const excludePath = path.join(gitDir, "info", "exclude");
  const ignoreEntry = `/${relativeStateDir}/`;
  const existing = await fs.readFile(excludePath, "utf8").catch(() => "");
  const lines = existing.split(/\r?\n/).map((line) => line.trim());
  if (lines.includes(ignoreEntry)) {
    return;
  }

  await fs.mkdir(path.dirname(excludePath), { recursive: true });
  const prefix = existing.length > 0 && !existing.endsWith("\n") ? "\n" : "";
  await fs.appendFile(excludePath, `${prefix}# Kitty runtime state\n${ignoreEntry}\n`, "utf8");
}

async function resolveGitDir(rootDir: string): Promise<string | null> {
  const dotGitPath = path.join(path.resolve(rootDir), ".git");
  const stat = await fs.stat(dotGitPath).catch(() => null);
  if (!stat) {
    return null;
  }

  if (stat.isDirectory()) {
    return dotGitPath;
  }

  if (!stat.isFile()) {
    return null;
  }

  const content = await fs.readFile(dotGitPath, "utf8").catch(() => "");
  const match = /^gitdir:\s*(.+)$/im.exec(content);
  if (!match?.[1]) {
    return null;
  }

  return path.resolve(rootDir, match[1].trim());
}
